var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ToastUtils from '@/utils/ToastUtils';
import useAppComponents from './useAppComponents';
/**
 * onMutate에서 즉시 toggle 할 대상 query key prefix.
 * 저장리스트가 크면 PlaceListDetail 재조회까지 수초 걸려서 "안 눌렸나?" 라고
 * 헷갈리는 QA 이슈가 있었음. 캐시의 isSaved 만 우선 뒤집고 onError 시 revert.
 *
 * 도메인 hook은 도메인만 책임진다 (CLAUDE.md `Hook 설계 원칙`):
 * 따라서 SavedPlaceLists 등 다른 도메인 query 의 invalidate 는 호출처가 책임진다.
 */
const PLACE_LIST_DETAIL_QUERY_KEY_PREFIX = 'PlaceListDetail';
export function useSavePlaceList(options) {
    const { api } = useAppComponents();
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ isSaved, placeListId, }) {
            if (isSaved) {
                return yield api.unsavePlaceList({ placeListId });
            }
            else {
                return yield api.savePlaceList({ placeListId });
            }
        }),
        // 서버 응답 + invalidate refetch 사이의 지연을 가리기 위해 캐시의 isSaved 만 즉시 toggle.
        // PlaceListDetail 캐시는 filters 까지 포함한 키 (['PlaceListDetail', placeListId, filters]).
        // 같은 placeListId 의 모든 filters 변형을 한 번에 뒤집기 위해 prefix match 로 순회한다.
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ isSaved, placeListId }) {
            yield queryClient.cancelQueries({
                queryKey: [PLACE_LIST_DETAIL_QUERY_KEY_PREFIX, placeListId],
            });
            const snapshots = [];
            queryClient
                .getQueryCache()
                .findAll({
                queryKey: [PLACE_LIST_DETAIL_QUERY_KEY_PREFIX, placeListId],
            })
                .forEach(query => {
                const prevData = query.state.data;
                snapshots.push({ queryKey: query.queryKey, data: prevData });
                if (prevData === null || prevData === void 0 ? void 0 : prevData.placeList) {
                    queryClient.setQueryData(query.queryKey, Object.assign(Object.assign({}, prevData), { placeList: Object.assign(Object.assign({}, prevData.placeList), { isSaved: !isSaved }) }));
                }
            });
            return { snapshots };
        }),
        onSuccess: (_data, variables) => {
            var _a;
            if (!variables.isSaved) {
                ToastUtils.show('리스트를 [메뉴 > 내 저장]에 저장했습니다.');
            }
            else {
                ToastUtils.show('리스트 저장을 해제했습니다.');
            }
            // PlaceListDetail invalidate 없음 — onMutate 의 setQueryData 가 isSaved 만 토글했고
            // 서버 응답이 204 (no body) 라 재조회로 갱신될 다른 필드가 없다.
            // SavedPlaceLists invalidate 도 없음 — 호출처 (SavedPlaceListsScreen 의 useFocusEffect) 가 책임.
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, variables);
        },
        onError: (error, _variables, context) => {
            // optimistic update revert.
            context === null || context === void 0 ? void 0 : context.snapshots.forEach(({ queryKey, data }) => {
                queryClient.setQueryData(queryKey, data);
            });
            ToastUtils.showOnApiError(error);
        },
    });
    const safeMutate = (args) => {
        if (isPending)
            return;
        mutate(args);
    };
    return safeMutate;
}
